import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';

// 안전한 localStorage 접근 함수 - AdminAuthContext와 동일한 방식
const safeLocalStorage = {
  getItem: (key: string): string | null => {
    try {
      const value = localStorage.getItem(key);
      console.log(`[DEBUG] AdminProtectedRoute localStorage.getItem(${key}):`, value ? value.substring(0, 20) + '...' : 'null');
      return value;
    } catch (error) {
      console.error('localStorage 접근 실패:', error);
      return null;
    }
  },
  setItem: (key: string, value: string): boolean => {
    try {
      console.log(`[DEBUG] AdminProtectedRoute localStorage.setItem(${key}):`, value.substring(0, 20) + '...');
      localStorage.setItem(key, value);
      
      // 저장 확인
      const savedValue = localStorage.getItem(key);
      const isSuccess = savedValue === value;
      console.log(`[DEBUG] AdminProtectedRoute localStorage.setItem(${key}) 결과:`, isSuccess ? '성공' : '실패');
      
      if (!isSuccess) {
        console.error(`[DEBUG] AdminProtectedRoute localStorage.setItem(${key}) 저장 확인 실패`);
        console.error('원본 값:', value);
        console.error('저장된 값:', savedValue);
      }
      
      return isSuccess;
    } catch (error) {
      console.error('localStorage 저장 실패:', error);
      return false;
    }
  },
  removeItem: (key: string): boolean => {
    try {
      console.log(`[DEBUG] AdminProtectedRoute localStorage.removeItem(${key})`);
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('localStorage 삭제 실패:', error);
      return false;
    }
  }
};

interface AdminProtectedRouteProps {
  children?: React.ReactNode;
}

const AdminProtectedRoute: React.FC<AdminProtectedRouteProps> = ({ children }) => {
  // 개발 모드에서 Mock 관리자 모드 체크 (useAdminAuth 호출 전에 체크)
  const isDevMode = process.env.NODE_ENV === 'development';
  const enableMockAdmin = localStorage.getItem('enableMockAdmin') === 'true';
  
  if (isDevMode && enableMockAdmin) {
    console.log('🛠️ [개발 모드] Mock 관리자 모드 - 인증 우회');
    // Mock 관리자 모드에서는 인증 체크를 건너뛰고 바로 접근 허용
    return <>{children || <Outlet />}</>;
  }

  const { currentAdmin, loading, isAuthenticated } = useAdminAuth();

  console.log('AdminProtectedRoute 렌더링:', {
    currentAdmin: currentAdmin?.username,
    loading,
    isAuthenticated,
    hasAdminToken: !!safeLocalStorage.getItem('adminToken'),
    hasToken: !!safeLocalStorage.getItem('token'),
    hasAccessToken: !!safeLocalStorage.getItem('accessToken'),
    hasCurrentAdmin: !!currentAdmin,
    adminTokenValue: safeLocalStorage.getItem('adminToken')?.substring(0, 20) + '...',
    isDevMode,
    enableMockAdmin
  });

  // 로딩 중이면 로딩 표시
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100%' 
      }}>
        <div>관리자 인증 확인 중...</div>
      </div>
    );
  }

  // 인증되지 않은 경우 로그인 페이지로 이동
  if (!isAuthenticated) {
    const hasAdminToken = !!safeLocalStorage.getItem('adminToken');
    // 관리자 토큰이 없으면 즉시 관리자 로그인으로 이동
    if (!hasAdminToken) {
      console.log('AdminProtectedRoute - 관리자 토큰 없음 → /admin/login 리다이렉트');
      return <Navigate to="/admin/login" replace />;
    }
    // 관리자 토큰은 있지만 인증 컨텍스트가 아직 준비되지 않은 경우 잠시 로딩
    if (loading || !currentAdmin) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100%' 
        }}>
          <div>관리자 인증 확인 중...</div>
        </div>
      );
    }
    // 토큰 있고 컨텍스트도 준비되면 접근 허용
    return <>{children || <Outlet />}</>;
  }

  // 인증된 관리자만 접근 허용
  console.log('AdminProtectedRoute - 인증된 관리자 접근 허용:', currentAdmin?.username);
  return <>{children || <Outlet />}</>;
};

export default AdminProtectedRoute; 